// TrainingMaterialsList.jsx — shows every file that has trained a bot
// (scripts, recordings, transcripts, few-shot examples) with preview + remove.
//
// Backed by /api/dialer/v1/bots/:botId/training-materials. Mounted from
// AIDialerBotTrainer.jsx once the bot has a saved _id.

import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText, FileAudio, FileVideo, Mic, MessageCircle, Trash2, Loader2,
  RefreshCcw, X, AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiDel } from '../lib/api.js';

const KIND_META = {
  script:     { icon: FileText,      label: 'Script' },
  recording:  { icon: FileVideo,     label: 'Recording' },
  transcript: { icon: FileAudio,     label: 'Transcript' },
  few_shot:   { icon: MessageCircle, label: 'Few-shot' },
  document:   { icon: FileText,      label: 'Document' },
};

function formatBytes(n) {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDuration(sec) {
  if (!sec || sec <= 0) return '';
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  if (h) return `${h}:${String(m).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
  return `${m}:${String(rest).padStart(2, '0')}`;
}

function timeAgo(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

export default function TrainingMaterialsList({ botId }) {
  const qc = useQueryClient();
  const [previewId, setPreviewId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['bot-training-materials', botId],
    queryFn: () => apiGet(`/dialer/v1/bots/${botId}/training-materials`),
    enabled: !!botId,
  });

  const items = data?.data || [];
  const total = data?.total ?? items.length;

  const deleteMut = useMutation({
    mutationFn: (id) => apiDel(`/dialer/v1/bots/${botId}/training-materials/${id}`),
    onSuccess: () => {
      toast.success('Training material removed');
      qc.invalidateQueries({ queryKey: ['bot-training-materials', botId] });
      qc.invalidateQueries({ queryKey: ['dialer-bot', botId] });
      setConfirmId(null);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Remove failed'),
  });

  return (
    <div className="panel p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-white">
            Training materials{' '}
            <span className="text-white/40 text-xs">({total})</span>
          </div>
          <div className="text-xs text-white/50">
            Every file uploaded to train this bot. Remove to stop using it.
          </div>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="text-xs text-civix-cyan hover:underline flex items-center gap-1 disabled:opacity-50"
        >
          {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-xs mb-3">
          <AlertCircle className="w-4 h-4 mt-px shrink-0" />
          <div className="flex-1">
            <div>Failed to load training history.</div>
            <button onClick={() => refetch()} className="underline mt-1">Retry</button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-md bg-white/5 animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div className="text-xs text-white/50 text-center py-6 px-3 border border-dashed border-white/10 rounded-lg">
          No training materials uploaded yet. Drop a script, recording, or transcript above to start training this bot.
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((m) => (
            <MaterialRow
              key={m._id}
              material={m}
              onPreview={() => setPreviewId(m._id)}
              onRemove={() => setConfirmId(m._id)}
            />
          ))}
        </div>
      )}

      {previewId && (
        <PreviewDrawer
          botId={botId}
          materialId={previewId}
          onClose={() => setPreviewId(null)}
        />
      )}

      {confirmId && (
        <ConfirmRemove
          name={items.find((m) => m._id === confirmId)?.title || 'this material'}
          busy={deleteMut.isPending}
          onCancel={() => !deleteMut.isPending && setConfirmId(null)}
          onConfirm={() => deleteMut.mutate(confirmId)}
        />
      )}
    </div>
  );
}

function MaterialRow({ material, onPreview, onRemove }) {
  const meta = KIND_META[material.kind] || KIND_META.document;
  const Icon = material.kind === 'recording' && material.mimeType?.startsWith('audio/')
    ? Mic
    : meta.icon;
  const parts = [
    meta.label,
    formatBytes(material.sizeBytes),
    formatDuration(material.durationSec),
    timeAgo(material.createdAt),
  ].filter(Boolean);

  return (
    <div className="rounded-md bg-white/5 border border-white/5 hover:border-white/10 px-3 py-3">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-md bg-civix-purple/15 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-civix-purple" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">
            {material.title || material.originalFilename || '(untitled)'}
          </div>
          {material.originalFilename && material.originalFilename !== material.title && (
            <div className="text-[11px] text-white/40 truncate">{material.originalFilename}</div>
          )}
          <div className="text-[11px] text-white/40 mt-0.5">
            {parts.join(' • ')}
          </div>
          {material.bodyPreview && (
            <div
              className="mt-2 text-[11px] text-white/50 font-mono leading-snug overflow-hidden"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {material.bodyPreview}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onPreview}
            className="text-xs px-2 py-1 rounded-sm text-white/70 hover:bg-white/10"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 text-white/40 hover:text-red-400"
            aria-label={`Remove ${material.title || 'material'}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewDrawer({ botId, materialId, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['bot-training-material', botId, materialId],
    queryFn: () => apiGet(`/dialer/v1/bots/${botId}/training-materials/${materialId}`),
  });
  const m = data?.data;
  const fullText = m?.body || m?.transcript || '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Training material preview"
      className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="panel w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">
              {m?.title || m?.originalFilename || 'Preview'}
            </div>
            {m && (
              <div className="text-[11px] text-white/40">
                {(KIND_META[m.kind] || KIND_META.document).label}
                {m.originalFilename ? ` • ${m.originalFilename}` : ''}
              </div>
            )}
          </div>
          <button ref={closeRef} onClick={onClose} aria-label="Close preview" className="text-white/50 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && <div className="text-xs text-white/40">Loading…</div>}
          {error && (
            <div className="text-xs text-red-300">Failed to load material.</div>
          )}
          {m && !fullText && (
            <div className="text-xs text-white/40">No text body stored for this material.</div>
          )}
          {fullText && (
            <pre className="text-xs text-white/80 font-mono whitespace-pre-wrap leading-relaxed">{fullText}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

function ConfirmRemove({ name, busy, onConfirm, onCancel }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    document.addEventListener('keydown', onKey);
    cancelRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="remove-material-title"
      className="fixed inset-0 z-70 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4"
      onClick={onCancel}
    >
      <div onClick={(e) => e.stopPropagation()} className="panel p-5 max-w-sm w-full space-y-4">
        <div id="remove-material-title" className="font-display font-bold text-lg text-white">
          Remove this material?
        </div>
        <div className="text-sm text-white/70">
          "{name}" will no longer be used to train this bot. If it was a few-shot example, it will also be removed from the bot's prompt context. This cannot be undone.
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button ref={cancelRef} onClick={onCancel} disabled={busy} className="btn-ghost">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-2 rounded-md text-sm font-medium text-white bg-rose-500/80 hover:bg-rose-500 disabled:opacity-60 flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}
